import {
  DocumentEnum,
  MatchDocumentAmountToleranceModeEnum,
  MatchDocumentDateBasisEnum,
  MatchDocumentDirectionModeEnum,
  MatchDocumentSettlementBasisEnum,
  MatchDocumentTextMatchModeEnum,
} from "@/__generated__/graphql";
import {
  ArrowLeftRightIcon,
  BanknoteIcon,
  CalendarCheckIcon,
  CalendarDaysIcon,
  CalendarRangeIcon,
  ChartPieIcon,
  CircleArrowDownIcon,
  CircleArrowUpIcon,
  CloudUploadIcon,
  CoinsIcon,
  CreditCardIcon,
  EyeOffIcon,
  FileMinusIcon,
  FilePlusIcon,
  FileTextIcon,
  HashIcon,
  PercentIcon,
  ReceiptIcon,
  ShoppingCartIcon,
  SparklesIcon,
  WalletIcon,
  type LucideIcon,
} from "lucide-react";

type SelectOption = {
  value: string;
  label: string;
  icon: LucideIcon;
  description: string;
};

export const DIRECTION_OPTIONS: readonly SelectOption[] = [
  {
    value: MatchDocumentDirectionModeEnum.PaymentsMatchBills,
    label: "Money out",
    icon: CircleArrowDownIcon,
    description:
      "Money leaving your bank account. Choose this when the rule should match checks and transfers to vendors you owe.",
  },
  {
    value: MatchDocumentDirectionModeEnum.ReceiptsMatchInvoices,
    label: "Money in",
    icon: CircleArrowUpIcon,
    description:
      "Money coming into your bank account. Choose this when the rule should match customer payments against invoices you sent.",
  },
  {
    value: MatchDocumentDirectionModeEnum.Either,
    label: "In or out",
    icon: ArrowLeftRightIcon,
    description:
      "Both money in and money out. Only use this for a shared operating account — most rules should pick money in or money out.",
  },
];

export const DOCUMENT_TYPE_OPTIONS: readonly SelectOption[] = [
  {
    value: DocumentEnum.Bill,
    label: "Bill",
    icon: ReceiptIcon,
    description:
      "A vendor bill you still owe. Include it when supplier invoices should be eligible to match outgoing payments.",
  },
  {
    value: DocumentEnum.PurchaseOrder,
    label: "Purchase order",
    icon: ShoppingCartIcon,
    description:
      "A purchase order before it is fully billed. Include it if you sometimes pay against open POs rather than final bills.",
  },
  {
    value: DocumentEnum.Receipt,
    label: "Receipt",
    icon: FileTextIcon,
    description:
      "A card or cash receipt for a purchase. Include it when small expenses are recorded as receipts instead of formal bills.",
  },
  {
    value: DocumentEnum.Check,
    label: "Check",
    icon: CreditCardIcon,
    description:
      "A check payment document. Include it when you issue or deposit checks that should match bank activity.",
  },
  {
    value: DocumentEnum.DebitNote,
    label: "Debit note",
    icon: FileMinusIcon,
    description:
      "A supplier debit note that reduces what you owe. Include it when vendor adjustments should match payments.",
  },
  {
    value: DocumentEnum.Invoice,
    label: "Invoice",
    icon: FileTextIcon,
    description:
      "A customer invoice you are waiting to be paid. Include it when incoming deposits should match sales you billed.",
  },
  {
    value: DocumentEnum.CreditNote,
    label: "Credit note",
    icon: FilePlusIcon,
    description:
      "A credit that reduces what a customer owes you. Include it when refunds or credits should match bank activity.",
  },
];

export const AMOUNT_TOLERANCE_MODE_OPTIONS: readonly SelectOption[] = [
  {
    value: MatchDocumentAmountToleranceModeEnum.Absolute,
    label: "Dollars",
    icon: CoinsIcon,
    description:
      "A fixed dollar wiggle room. Choose this when a few cents or dollars off is normal, like bank fees or rounding.",
  },
  {
    value: MatchDocumentAmountToleranceModeEnum.Percent,
    label: "Percent",
    icon: PercentIcon,
    description:
      "A percentage wiggle room. Choose this when larger documents can be off by more dollars but the same share of the total.",
  },
];

export const MATCH_DATE_BASIS_OPTIONS: readonly SelectOption[] = [
  {
    value: MatchDocumentDateBasisEnum.Issued,
    label: "Bill date",
    icon: CalendarDaysIcon,
    description:
      "The date the document was issued. Choose this when payment timing should be measured from invoice or bill date.",
  },
  {
    value: MatchDocumentDateBasisEnum.Due,
    label: "Due date",
    icon: CalendarCheckIcon,
    description:
      "The date payment is due. Choose this when you expect bank activity near the due date, not the issue date.",
  },
  {
    value: MatchDocumentDateBasisEnum.PaymentTerms,
    label: "Payment terms",
    icon: CalendarRangeIcon,
    description:
      "Negotiated payment terms on the document, when that field is set. Choose this when Net 30, Net 60, or similar terms define when payment should arrive.",
  },
  {
    value: MatchDocumentDateBasisEnum.UploadDate,
    label: "Upload date",
    icon: CloudUploadIcon,
    description:
      "Only used when no bill, due, or payment-term date is available on the document.",
  },
];

export const TEXT_MATCH_MODE_OPTIONS: readonly SelectOption[] = [
  {
    value: MatchDocumentTextMatchModeEnum.Off,
    label: "Don't use text",
    icon: EyeOffIcon,
    description:
      "Ignore bank and document wording. Use amount and date matching only.",
  },
  {
    value: MatchDocumentTextMatchModeEnum.Reference,
    label: "Reference numbers",
    icon: HashIcon,
    description: "Match invoice or PO numbers in the bank text.",
  },
  {
    value: MatchDocumentTextMatchModeEnum.Semantic,
    label: "Smart match",
    icon: SparklesIcon,
    description:
      "Compare bank and document wording, including similar or paraphrased text.",
  },
];

export const SETTLEMENT_BASIS_OPTIONS: readonly SelectOption[] = [
  {
    value: MatchDocumentSettlementBasisEnum.Total,
    label: "Full document amount",
    icon: BanknoteIcon,
    description:
      "Match when the bank line equals the document total — one payment clears the bill.",
  },
  {
    value: MatchDocumentSettlementBasisEnum.Balance,
    label: "Remaining balance",
    icon: WalletIcon,
    description:
      "Match when the bank line equals what's still owed — typical for the final payment.",
  },
  {
    value: MatchDocumentSettlementBasisEnum.BalancePartial,
    label: "Partial payment",
    icon: ChartPieIcon,
    description:
      "Match when the bank line and one or two others like it come to what's owed on an invoice or bill — paid in two or three parts.",
  },
];
